import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../shell/PublicLayout";
import Badge from "../components/Badge";
import ListRow from "../components/ListRow";
import StatusChip from "../components/StatusChip";
import EmptyState from "../components/EmptyState";
import Button from "../components/Button";
import { SearchIcon } from "../components/icons";
import { TagLabel } from "./ClassPage";
import { DesignedNotFound } from "./PermalinkPage";
import { fetchTagShelf } from "../api/tagShelf";
import { paths } from "../routing/paths";

/*
 * Tag page — the topic shelf a class-page tag pill opens (brief §6.3's
 * Explore layer: Simcha, Bitachon, Davening…). Replaces the legacy
 * SearchTags page at /tag/:uniqueName. Same grammar as a list-mode
 * category: breadcrumb, navy hero (tag tile, TOPIC chip, honest count),
 * one white card of class rows, "Show more" against the server's 20-row
 * pages. Rows carry no media/duration fields, so the meta line is the
 * posted date only — labels never claim what the data can't support.
 */

const tagIcon = (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z" />
    <circle cx="7" cy="7" r="1.5" fill="#3B82F6" stroke="none" />
  </svg>
);

export default function TagPage() {
  const { uniqueName } = useParams();
  const navigate = useNavigate();
  const [shelf, setShelf] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShelf(null);
    setFailed(false);
    fetchTagShelf(uniqueName, 0).then((first) => {
      if (cancelled) return;
      if (!first) setFailed(true);
      else setShelf(first);
    });
    return () => {
      cancelled = true;
    };
  }, [uniqueName]);

  const loadMore = () => {
    if (!shelf || loadingMore) return;
    setLoadingMore(true);
    fetchTagShelf(uniqueName, shelf.page + 1).then((next) => {
      setLoadingMore(false);
      if (!next) return; // keep what we have; the button stays for a retry
      setShelf((prev) => ({ ...next, items: [...prev.items, ...next.items] }));
    });
  };

  // The endpoint 500s on an unknown tag — same designed 404 as a bad
  // archive permalink (never blank).
  if (failed) return <DesignedNotFound node={`tag/${uniqueName}`} />;

  const hasMore = shelf ? shelf.page + 1 < shelf.lastPage : false;
  // The paginator total counts unlisted rows too; once every page is
  // loaded the filtered length is the honest figure, so prefer it.
  const count = shelf ? (hasMore ? shelf.total : shelf.items.length) : 0;
  const countLine = shelf ? `${count.toLocaleString()} ${count === 1 ? "class" : "classes"} · Topic shelf` : null;

  return (
    <PublicLayout>
      {shelf?.name && (
        <Helmet>
          <title>{`${shelf.name} | Inside Chassidus`}</title>
        </Helmet>
      )}
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1 ic-wrap-anywhere">
            {shelf?.name || "Tag"}
          </span>
        </nav>

        {/* Loading skeleton */}
        {shelf === null && (
          <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading tagged classes">
            <div className="h-44 rounded-xl bg-ic-lightGray animate-pulse" />
            <div className="h-96 rounded-xl bg-ic-lightGray animate-pulse" />
          </div>
        )}

        {shelf !== null && (
          <>
            {/* Tag hero — navy, list-category grammar */}
            <section
              className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex gap-5 items-start flex-wrap"
              style={{ padding: "clamp(20px, 5vw, 32px)" }}
            >
              <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-ic-navyCard hidden xs:flex items-center justify-center">
                {tagIcon}
              </div>
              <div className="flex-1 min-w-[240px] flex flex-col gap-2">
                <span className="self-start">
                  <Badge variant="onNavy">TOPIC</Badge>
                </span>
                <h1 className="m-0 font-bold text-white tracking-[-0.4px] ic-wrap-anywhere" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
                  <TagLabel name={shelf.name || uniqueName} hebrewClass="text-ic-sky" />
                </h1>
                <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">
                  Every class in the archive tagged with this topic, newest alongside decades of
                  classics.
                </p>
                {countLine && <span className="text-[14px] leading-5 text-ic-lightGray">{countLine}</span>}
              </div>
            </section>

            {/* Class rows */}
            {shelf.items.length > 0 && (
              <section className="flex flex-col gap-3">
                <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                  {shelf.items.map((c, i) => (
                    <ListRow
                      key={c.id}
                      chip={c.isNew ? <StatusChip kind="new" size="sm">NEW</StatusChip> : undefined}
                      title={
                        <Link to={paths.permalink(c.slug)} className="text-inherit no-underline hover:text-ic-blue hover:no-underline">
                          {c.title}
                        </Link>
                      }
                      meta={c.dateLabel}
                      actionLabel={`Play ${c.title}`}
                      onAction={() => navigate(paths.permalink(c.slug))}
                      last={i === shelf.items.length - 1}
                      className="!py-3.5 !pl-5 !pr-4 !gap-3"
                    />
                  ))}
                </div>
                <div className="flex flex-col items-center gap-2 pt-1">
                  {hasMore && (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="bg-transparent text-ic-blue font-sans text-[16px] font-bold leading-6 h-12 px-8 border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:bg-[rgba(24,108,206,0.05)] focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft disabled:opacity-60 disabled:cursor-wait"
                    >
                      {loadingMore ? "Loading…" : "Show more classes"}
                    </button>
                  )}
                  <span className="text-[14px] leading-5 text-ic-slate text-center">
                    {hasMore
                      ? `Showing the first ${shelf.items.length} classes`
                      : `Showing all ${shelf.items.length} ${shelf.items.length === 1 ? "class" : "classes"} with this tag`}
                  </span>
                </div>
              </section>
            )}

            {/* Designed empty state — never blank */}
            {shelf.items.length === 0 && (
              <EmptyState
                icon={<SearchIcon size={28} />}
                title="Nothing is filed under this tag yet"
                body="With 15,000+ classes in the archive, what you're looking for almost certainly exists — search will find it."
                actions={
                  <Link to={paths.search()}>
                    <Button size="lg">Search the archive</Button>
                  </Link>
                }
              />
            )}
          </>
        )}
      </main>
    </PublicLayout>
  );
}
